// The business figures as tiles (Session ADC moved them into the dashboard; Session SC7B, the calm screens part 2,
// moved them here so TWO screens show the same tiles from the same code).
//
//  - The DASHBOARD asks one question, "what needs me now", so it shows only the tiles whose figure needs
//    attention (mode "attention"): at most SIX, most urgent first, or one plain line when nothing does. The rest
//    is one quiet "All figures" link.
//  - PRIORITIES (/office/reporting) keeps EVERY tile under "All figures" (mode "all"), in the original order,
//    exactly as the dashboard used to show them.
//
// NO TILE'S CALCULATION CHANGED. Each tile below is the value, hint, accent, tone and link it always was; the only
// thing added is whether it needs attention, derived from the tile's OWN tone or a non-zero waiting count, nothing
// else. The helpers that word a tile are exported unchanged (OfficeDashboard.tsx re-exports them for its tests).
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { DashboardTiles } from "../../lib/api";
import { Loader } from "../../components/Loader";
import { toneTextClass } from "../../components/StatusPill";
import { formatDateNumeric, formatDateTime } from "../../lib/format";

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

// The Xero-sync freshness of the invoice figures (invoicesSyncedAt, set by the daily cron and the
// manual Sync now). Session AEH A5: this is now folded INTO the Invoices overdue tile hint (the
// figure it describes), so it is worded as a lower-case clause after the invoice count rather than
// as a separate grid-wide caption.
function lastSyncedLabel(iso: string | null): string {
  if (!iso) return "not yet synced from Xero";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "not yet synced from Xero";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return "synced just now";
  if (mins < 60) return `synced ${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `synced ${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `synced ${days} day${days === 1 ? "" : "s"} ago`;
}

// The Invoices overdue tile hint: the overdue-invoice count with the Xero-sync freshness folded in
// (Session AEH A5), so the figure and its provenance read together on the tile that shows them.
function invoicesOverdueHint(count: number, syncedAt: string | null): string {
  return `${count} ${count === 1 ? "invoice" : "invoices"}, ${lastSyncedLabel(syncedAt)}`;
}

// The Entrapments tile hint. Session ADU: the average is measured slaStartAt -> workFinishedAt
// (the locked SLA clock rule) over the measurable rows only, so the hint must say when it is
// describing a subset. The rule is two-part: unmeasured rows are excluded from the average AND
// counted openly. A silent average over 2 of 5 entrapments would read as though it covered all 5.
// Pure and exported so the honesty cases are tested directly.
export function entrapmentHint(e: { count: number; avgResolutionMins: number | null; measured: number }): string {
  if (e.count === 0) return "This month";
  if (e.avgResolutionMins == null) return "This month, none measured yet";
  if (e.measured < e.count) return `This month, avg ${e.avgResolutionMins} min (${e.measured} of ${e.count} measured)`;
  return `This month, avg ${e.avgResolutionMins} min`;
}

// The Renewal cycles tile hint (Session APA). The number counts every renewal-cycle finding; the hint
// names how many are a CPI rise applied with no record the customer was sent the new charge, so that finding is never
// filed under "missed" on the office's first screen. "No record", never "not told": the platform knows
// only what the Sent folder and its own sends show, and a letter posted or a phone call leaves neither.
// Session APB: "told" now means SENT THE NEW CHARGE (an email stating both figures, Director decision 1),
// so the hint says exactly that: a customer sent only an invitation is still counted here.
// Pure and exported so the wording is tested directly.
export function renewalCyclesHint(attention: number, notTold: number): string {
  if (notTold <= 0) return "Missed, stalled, or no record the new charge was sent";
  const told = `${notTold} priced, no record the new charge was sent`;
  const others = attention - notTold;
  return others > 0 ? `${told}; ${others} missed or stalled` : told;
}

// The Subcontractor payments tile (Session ARZ, Director decisions D-SBD-3 and D-SBD-4). Counts over ACTIVE
// subcontractors only, never a bank detail. The number is how many need a person about their payment details; the
// hint names every count, zero as 0, so the office can see the check ran. `unverified` already includes the
// CHANGED ones, which the hint calls out because a change nobody has verified is a possible fraud.
// Pure and exported so the wording is tested directly.
export type SubcontractorPaymentCounts = NonNullable<DashboardTiles["subcontractorPaymentDetails"]>;

export function subcontractorPaymentsAttention(p: SubcontractorPaymentCounts): number {
  return p.noDetails + p.unverified + p.verifiedNotInXero;
}

export function subcontractorPaymentsHint(p: SubcontractorPaymentCounts): string {
  return `${p.noDetails} no details, ${p.unverified} unverified (${p.changedUnverified} changed), ${p.verifiedNotInXero} not in Xero`;
}

// Red when a CHANGED set is waiting for its call-back (a possible fraud), amber for any other gap, white at zero.
export function subcontractorPaymentsTone(p: SubcontractorPaymentCounts): "red" | "amber" | "white" {
  if (p.changedUnverified > 0) return "red";
  return subcontractorPaymentsAttention(p) > 0 ? "amber" : "white";
}

// A checked-at INSTANT is shown as the London calendar day it fell on, DD/MM/YYYY (formatDateNumeric).

// The Xero billing out-of-step tile's value + hint (Session ARY, Item 4). "Not checked" is a STATE,
// never a number: a genuinely healthy book (outOfStep 0) and a book the daily sweep has not reached
// in days must never render the same reassuring "0" (RULE 6 -- a fact this platform can compute live
// is never silently guessed at when the check itself has gone stale). Pure and exported so the
// wording is tested directly.
export function xeroBillingTileContent(t: { outOfStep: number; notLinked: number; checkedAt: string | null; stale: boolean; error: string | null }): {
  value: string;
  hint: string;
} {
  if (t.stale || t.checkedAt == null) {
    const when = t.checkedAt ? `not checked since ${formatDateNumeric(t.checkedAt)}` : "not checked yet";
    const hint = t.error ? `${when}: ${t.error}` : when;
    // Contracts already known to need a person are still counted (review finding); a zero is never shown as the
    // resting value, because nothing verified it.
    if (t.outOfStep > 0) return { value: String(t.outOfStep), hint: `${hint}, so there may be more` };
    return { value: "Not checked", hint };
  }
  const hint =
    t.notLinked > 0
      ? `${t.notLinked} active contract${t.notLinked === 1 ? "" : "s"} with no template linked`
      : "every active contract has a template linked";
  return { value: String(t.outOfStep), hint };
}

// The Email engine tile (Session ASO). Like the Xero billing tile above, "Not checked" is a STATE, never a number: an
// engine that has stopped reading the mailbox must never show the same resting 0 as a quiet morning. A message past the
// 10-minute deadline with no outcome is a FAULT and turns the tile red. Pure and exported so the wording is tested.
export function emailEngineTileContent(t: NonNullable<DashboardTiles["emailEngine"]>): {
  value: string;
  hint: string;
  tone: "red" | "amber" | "white";
} {
  const stale = !t.started || t.stale;
  const when = !t.started ? "not started yet" : t.polledAt ? `not checked since ${formatDateTime(t.polledAt)}` : "not checked yet";
  // A FAULT IS RED WHETHER OR NOT THE ENGINE IS RUNNING (Session ASO review, house rules MEDIUM 3): a stopped engine does
  // not make an overdue message any less overdue, so the fault count leads, and the hint also says it is not checking.
  if (t.overdue > 0) {
    const also = stale ? `; also ${when}${t.pollError ? `: ${t.pollError}` : ""}` : "";
    return { value: String(t.overdue), hint: `past the 10 minute deadline with no outcome${also}`, tone: "red" };
  }
  if (stale) {
    return { value: "Not checked", hint: `${when}${t.pollError ? `: ${t.pollError}` : ""}`, tone: "amber" };
  }
  const d = t.last24h;
  const lines = t.proposedStandardLines > 0 ? `; ${t.proposedStandardLines} standard line${t.proposedStandardLines === 1 ? "" : "s"} proposed` : "";
  return {
    value: String(d.needsYou),
    hint: `${d.drafted} drafted, ${d.needsYou} need you, ${d.noReplyNeeded} need no reply (24 hours)${lines}`,
    tone: d.needsYou > 0 ? "amber" : "white",
  };
}

// The Data retention tile (Session ASQ, GDPR Stream G, Item 4; sent to an ADMIN only). The number is how many records the
// weekly sweep has proposed for deletion or anonymising and nobody has confirmed or dismissed yet. Like the Xero billing
// and Email engine tiles, "Not checked" is a STATE, never a number: a figure that could not be read, or a sweep that has
// not run for over a week, must never show the same resting 0 as a clean list. Nor may a sweep that could not read every
// record or work out every rule (review F1): with nothing open it reads "Not fully checked", and a count it did find
// carries the caveat. Records with no retention date to count from are kept and never proposed (review F4), so the hint
// says how many, rather than letting them read as part of an all-clear. Pure and exported so the wording is tested.
export function dataRetentionTileContent(t: NonNullable<DashboardTiles["dataRetention"]>): { value: string; hint: string; tone: "amber" | "white" } {
  if (t.open === null) return { value: "Not checked", hint: "the proposals could not be read just now", tone: "amber" };
  const awaiting = `${t.open} record${t.open === 1 ? "" : "s"} past how long we keep ${t.open === 1 ? "it" : "them"}, awaiting your confirmation`;
  if (t.stale) {
    const when = t.lastSweepAt ? `the weekly sweep has not run since ${formatDateNumeric(t.lastSweepAt)}` : "the weekly sweep has not run yet";
    if (t.open > 0) return { value: String(t.open), hint: `${awaiting}; ${when}`, tone: "amber" };
    return { value: "Not checked", hint: when, tone: "amber" };
  }
  const caveat = "the last weekly check could not read every record";
  if (t.incomplete) {
    if (t.open > 0) return { value: String(t.open), hint: `${awaiting}; ${caveat}, so more may be due`, tone: "amber" };
    return { value: "Not fully checked", hint: `${caveat}, so some may be due`, tone: "amber" };
  }
  const noDate = t.notApplicable ? `; ${t.notApplicable} record${t.notApplicable === 1 ? "" : "s"} kept with no retention date` : "";
  if (t.open === 0) return { value: "0", hint: `nothing awaiting your confirmation${noDate}`, tone: "white" };
  return { value: String(t.open), hint: `${awaiting}${noDate}`, tone: "amber" };
}

// Session ACG KPI treatment: each tile carries a locked accent (the 1px fading top-edge
// gradient) and a value tone. Green = positive, purple = neutral/informational, red = urgent,
// amber = attention. The value only takes the accent's colour when the figure genuinely
// carries that state (an overdue £0 stays white -- colour is used with intention).
type Accent = "green" | "purple" | "red" | "amber";
type ValueTone = "green" | "white" | "red" | "amber";

// Theme-aware inks (a hand-typed #c0392b / #b8860b failed AA on the dark theme): the shared
// toneTextClass for the three coloured tones, the strong text ink for "white".
const VALUE_INK: Record<ValueTone, string> = {
  green: toneTextClass("green"),
  white: "text-ele-strong",
  red: toneTextClass("red"),
  amber: toneTextClass("amber"),
};

type TileLink = { kind: "external"; href: string } | { kind: "internal"; to: string } | null;

function Tile({
  label,
  value,
  hint,
  accent,
  valueTone,
  link,
}: {
  label: string;
  value: string;
  hint: string;
  accent: Accent;
  valueTone: ValueTone;
  link?: TileLink;
}) {
  const card = `ele-kpi ele-kpi--${accent}`;
  // A linked tile shows a purple direction arrow (navigation intent; an outward arrow for
  // the external SIM portal) and washes on hover (a.ele-kpi:hover in the CSS). A non-link
  // tile is inert.
  const inner = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="ele-kpi__label">{label}</span>
        {link && (
          <span aria-hidden className="text-[11px] font-semibold text-ele-purple">
            {link.kind === "external" ? "↗" : "→"}
          </span>
        )}
      </div>
      <div className={`ele-kpi__value mt-2 ${VALUE_INK[valueTone]}`}>{value}</div>
      <div className="ele-kpi__sub">{hint}</div>
    </>
  );

  if (link?.kind === "external") {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className={card}>
        {inner}
      </a>
    );
  }
  if (link?.kind === "internal") {
    return (
      <Link to={link.to} className={card}>
        {inner}
      </Link>
    );
  }
  return <div className={card}>{inner}</div>;
}

// ---- Which tiles need attention (SC7B) ----

export type KpiTileKey =
  | "arr"
  | "services-due"
  | "invoices-overdue"
  | "accounts-on-stop"
  | "renewals-due"
  | "sim-expiry"
  | "contracts-not-started"
  | "price-rise-due"
  | "renewal-cycles"
  | "xero-billing"
  | "email-engine"
  | "data-retention"
  | "flagged-clients"
  | "entrapments"
  | "subcontractor-payments";

// THE ORDER A DASHBOARD SHOWS ATTENTION TILES IN, most urgent first: ONE named list in ONE place (the Director's
// order, SC7B). A key left out of it is never shown on the dashboard, whatever its figure, so ARR and Services due
// (informational, they are never a thing that needs the office) are deliberately not in it: they live on Priorities.
export const ATTENTION_ORDER: readonly KpiTileKey[] = [
  "entrapments",
  "email-engine",
  "xero-billing",
  "invoices-overdue",
  "accounts-on-stop",
  "flagged-clients",
  "renewal-cycles",
  "subcontractor-payments",
  "data-retention",
  "renewals-due",
  "sim-expiry",
  "price-rise-due",
  "contracts-not-started",
];

// The most tiles the dashboard ever shows. More than this need attention only on a bad day, and the rest are one press
// away under "All figures", with their count said on the link.
export const ATTENTION_MAX_TILES = 6;

export interface KpiTileModel {
  key: KpiTileKey;
  label: string;
  value: string;
  hint: string;
  accent: Accent;
  valueTone: ValueTone;
  link: TileLink;
  /** True when the tile's OWN tone is amber or red, or the thing it counts has a non-zero waiting count. */
  attention: boolean;
  /**
   * What the DASHBOARD shows instead of the tile's full-set wording, when the reason it needs attention is not the figure the
   * full set shows (SC7B review): an entrapment still open from last month is red on the dashboard with its open count, though
   * "this month" reads 0. Priorities (All figures) never uses it.
   */
  onDashboard?: Partial<Pick<KpiTileModel, "label" | "value" | "hint" | "valueTone" | "accent">>;
}

/**
 * Every tile the server sent, in the original order, each with whether it needs attention. Pure. A tile whose figure
 * an older server (or the office role) does not send is absent, never a zero nobody measured.
 *
 * THE ATTENTION RULE, per tile (decision D-SC7B-2): the tile is red or amber on its own terms, or it counts something
 * waiting and the count is above zero. ARR and Services due are informational and never need attention.
 */
export function kpiTileModels(t: DashboardTiles): KpiTileModel[] {
  const out: KpiTileModel[] = [];
  out.push({
    key: "arr",
    label: "ARR",
    value: gbp(t.arr),
    hint: "Active contracts",
    accent: "green",
    valueTone: "green",
    link: { kind: "internal", to: "/office/contracts" },
    attention: false,
  });
  out.push({
    key: "services-due",
    label: "Services due",
    value: String(t.servicesDueThisMonth),
    hint: "This month",
    accent: "purple",
    valueTone: "white",
    link: t.servicesDueThisMonth > 0 ? { kind: "internal", to: "/office/lifts?filter=due-this-month" } : null,
    attention: false,
  });
  out.push({
    key: "invoices-overdue",
    label: "Invoices overdue",
    value: gbp(t.invoicesOverdue.amount),
    hint: invoicesOverdueHint(t.invoicesOverdue.count, t.invoicesSyncedAt),
    accent: "red",
    valueTone: t.invoicesOverdue.amount > 0 ? "red" : "white",
    link: t.invoicesOverdue.count > 0 ? { kind: "internal", to: "/office/invoices" } : null,
    attention: t.invoicesOverdue.amount > 0 || t.invoicesOverdue.count > 0,
  });
  // Session SC9B: customers ON STOP (Xero reports an invoice more than 30 days past its due date). Absent on an older server; a count of null is
  // "not checked" (stale or failed invoice read), never a zero. On the dashboard only when above zero; it links to the aged debtors view on stop.
  if (t.accountsOnStop) {
    const n = t.accountsOnStop.count;
    out.push({
      key: "accounts-on-stop",
      label: "Accounts on stop",
      value: n == null ? "Not checked" : String(n),
      hint: n == null ? "The invoice read is out of date" : "More than 30 days past due",
      accent: "red",
      valueTone: n != null && n > 0 ? "red" : "white",
      link: n != null && n > 0 ? { kind: "internal", to: "/office/debtors?onStop=true" } : null,
      attention: n != null && n > 0,
    });
  }
  out.push({
    key: "renewals-due",
    label: "Renewals due",
    value: String(t.renewalsDue90),
    hint: "Next 90 days",
    accent: "amber",
    valueTone: t.renewalsDue90 > 0 ? "amber" : "white",
    link: t.renewalsDue90 > 0 ? { kind: "internal", to: "/office/contracts?filter=due-90-days" } : null,
    attention: t.renewalsDue90 > 0,
  });
  out.push({
    key: "sim-expiry",
    label: "SIM expiry",
    value: String(t.simExpiryAlerts),
    hint: "Next 90 days",
    accent: "amber",
    valueTone: t.simExpiryAlerts > 0 ? "amber" : "white",
    // Always a link -- the SIM management portal is relevant at 0 too.
    link: { kind: "external", href: "https://simsinlifts.co.uk" },
    attention: t.simExpiryAlerts > 0,
  });
  out.push({
    key: "contracts-not-started",
    label: "Contracts not started",
    value: String(t.contractsNotStarted),
    hint: "Awaiting commencement",
    accent: "purple",
    valueTone: "white",
    link: t.contractsNotStarted > 0 ? { kind: "internal", to: "/office/contracts" } : null,
    attention: t.contractsNotStarted > 0,
  });
  out.push({
    key: "price-rise-due",
    label: "Price-rise due",
    value: String(t.priceRiseDue),
    hint: "Next 30 days",
    accent: "amber",
    valueTone: t.priceRiseDue > 0 ? "amber" : "white",
    link: t.priceRiseDue > 0 ? { kind: "internal", to: "/office/renewals" } : null,
    attention: t.priceRiseDue > 0,
  });
  // Session AMW: the renewal-cycle fail-safe. The two renewal tiles above are forward-only
  // windows, so a review that has already been missed drops OFF them at the moment it
  // matters most. This counts the contracts whose most recent anniversary was missed, is
  // waiting on a proposal, or was settled without the renewal date moving on, keyed on each
  // contract's real first-term end rather than the renewal date field. Session APA adds a
  // CPI rise applied with no record the customer was sent the new charge, named in the hint.
  const cycles = t.renewalCyclesAttention ?? 0;
  out.push({
    key: "renewal-cycles",
    label: "Renewal cycles",
    value: String(cycles),
    hint: renewalCyclesHint(cycles, t.renewalCyclesNotTold ?? 0),
    accent: "red",
    valueTone: cycles > 0 ? "red" : "white",
    link: cycles > 0 ? { kind: "internal", to: "/office/renewals" } : null,
    attention: cycles > 0,
  });
  // Session ARY, Item 4: absent on an older server response (RULE: optional fields never crash a page), so
  // the tile does not render at all rather than showing a false zero.
  if (t.xeroBilling) {
    const x = t.xeroBilling;
    const content = xeroBillingTileContent(x);
    const notChecked = x.stale || x.checkedAt == null;
    const hasIssue = x.outOfStep > 0 || x.notLinked > 0 || notChecked;
    out.push({
      key: "xero-billing",
      label: "Xero billing out of step",
      value: content.value,
      hint: content.hint,
      accent: notChecked ? "amber" : x.outOfStep > 0 ? "red" : "purple",
      valueTone: notChecked ? "amber" : x.outOfStep > 0 ? "red" : "white",
      link: hasIssue ? { kind: "internal", to: "/office/contracts?filter=xero-billing" } : null,
      // Its own tone: red when something is out of step, amber when the check did not run. Contracts with no
      // template linked are in the hint only (the tile stays white), so they do not claim the dashboard.
      attention: notChecked || x.outOfStep > 0,
    });
  }
  // Session ASO: the email engine. Absent on an older server, so no tile rather than a zero nobody measured.
  if (t.emailEngine) {
    const e = t.emailEngine;
    const content = emailEngineTileContent(e);
    out.push({
      key: "email-engine",
      label: "Email engine",
      value: content.value,
      hint: content.hint,
      accent: content.tone === "red" ? "red" : content.tone === "amber" ? "amber" : "purple",
      valueTone: content.tone,
      link: { kind: "internal", to: e.overdue > 0 ? "/office/mail?tab=all&outcome=pending" : "/office/mail" },
      // NEEDS ME NOW (SC7B review): "N need you" counts outcomes in the last 24 hours, not messages still open, so it is NOT a reason for
      // the dashboard. A message past its 10 minute deadline (a fault) or an engine that is not checking is. Mail's own lane count,
      // the morning list and the briefing carry the waiting replies.
      attention: e.overdue > 0 || !e.started || e.stale,
    });
  }
  // Session ASQ (GDPR): open retention proposals. The server sends it to an admin only, so an office user (and an
  // older server) sees no tile rather than a count they cannot act on.
  if (t.dataRetention) {
    const content = dataRetentionTileContent(t.dataRetention);
    out.push({
      key: "data-retention",
      label: "Data retention",
      value: content.value,
      hint: content.hint,
      accent: content.tone === "amber" ? "amber" : "purple",
      valueTone: content.tone,
      link: { kind: "internal", to: "/office/data-retention" },
      attention: content.tone === "amber",
    });
  }
  out.push({
    key: "flagged-clients",
    label: "Flagged clients",
    value: String(t.atRiskClients),
    hint: "One or more open risk flags",
    accent: "red",
    valueTone: t.atRiskClients > 0 ? "red" : "white",
    link: t.atRiskClients > 0 ? { kind: "internal", to: "/office/customers?filter=at-risk" } : null,
    attention: t.atRiskClients > 0,
  });
  out.push({
    key: "entrapments",
    label: "Entrapments",
    value: String(t.entrapments.count),
    hint: entrapmentHint(t.entrapments),
    accent: "red",
    valueTone: t.entrapments.count > 0 ? "red" : "white",
    // Always a link when anything is open, even if this month's count is 0 (an entrapment still open from last month).
    link: t.entrapments.count > 0 || (t.entrapments.open ?? 0) > 0 ? { kind: "internal", to: "/office/callouts" } : null,
    // NEEDS ME NOW (SC7B review): a month count would keep a red tile after an entrapment was cleared on the 2nd, so the dashboard reads
    // the figure that is still OPEN (any date). An older server that sends no open figure falls back to the month count: an open
    // entrapment is never hidden.
    attention: (t.entrapments.open ?? t.entrapments.count) > 0,
    onDashboard:
      t.entrapments.open !== undefined
        ? {
            label: "Entrapments open",
            value: String(t.entrapments.open),
            hint: t.entrapments.open === 1 ? "1 not yet closed, any date" : `${t.entrapments.open} not yet closed, any date`,
            valueTone: t.entrapments.open > 0 ? "red" : "white",
          }
        : undefined,
  });
  // Session ARZ: payment (bank) details across active subcontractors. Shown only when the server sent the
  // counts, so an older server shows no tile rather than a zero nobody measured. Always linked: the
  // Subcontractors page is where every one of these is put right.
  if (t.subcontractorPaymentDetails) {
    const p = t.subcontractorPaymentDetails;
    out.push({
      key: "subcontractor-payments",
      label: "Subcontractor payments",
      value: String(subcontractorPaymentsAttention(p)),
      hint: subcontractorPaymentsHint(p),
      accent: "amber",
      valueTone: subcontractorPaymentsTone(p),
      link: { kind: "internal", to: "/office/subcontractors" },
      attention: subcontractorPaymentsAttention(p) > 0,
    });
  }
  return out;
}

/**
 * The tiles a dashboard shows: only those that need attention, in ATTENTION_ORDER, at most ATTENTION_MAX_TILES, plus how
 * many more needed attention but did not fit. Pure.
 */
export function attentionTiles(models: KpiTileModel[], max = ATTENTION_MAX_TILES): { shown: KpiTileModel[]; hiddenCount: number } {
  const rank = new Map<KpiTileKey, number>(ATTENTION_ORDER.map((k, i) => [k, i]));
  const needing = models.filter((m) => m.attention && rank.has(m.key)).sort((a, b) => rank.get(a.key)! - rank.get(b.key)!);
  return { shown: needing.slice(0, max), hiddenCount: Math.max(0, needing.length - max) };
}

export const NOTHING_NEEDS_ATTENTION = "Nothing needs your attention right now.";

function TileCard({ m, dashboard = false }: { m: KpiTileModel; dashboard?: boolean }) {
  const v = dashboard && m.onDashboard ? { ...m, ...m.onDashboard } : m;
  return <Tile label={v.label} value={v.value} hint={v.hint} accent={v.accent} valueTone={v.valueTone} link={v.link} />;
}

/**
 * The business tiles, self-fetching (GET /api/dashboard, unchanged). mode "attention" is the dashboard (what needs
 * the office now, at most six, most urgent first, else one line, then a quiet "All figures" link); mode "all" is
 * Priorities (every tile, as the dashboard used to show them).
 */
export function KpiTileSet({ mode, refreshKey = 0 }: { mode: "attention" | "all"; refreshKey?: number }): ReactNode {
  const [tiles, setTiles] = useState<DashboardTiles | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    // Promise.resolve().then keeps a missing client (a test that mocks only part of the api) a handled rejection.
    Promise.resolve()
      .then(() => api.dashboard.get())
      .then((t) => {
        if (live) setTiles(t);
      })
      .catch(() => {
        if (live) setError(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [refreshKey]);

  return (
    // No margin classes on the wrapper: the pre-ADC page rendered this state machine bare, so
    // the block spacing (the caption's own height before the next section) is preserved exactly.
    <div>
      {loading ? (
        <Loader shape="inline" />
      ) : error ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the dashboard. Please try again in a moment.
        </p>
      ) : tiles ? (
        mode === "all" ? (
          <TileGrid>
            {kpiTileModels(tiles).map((m) => (
              <TileCard key={m.key} m={m} />
            ))}
          </TileGrid>
        ) : (
          <AttentionTiles tiles={tiles} />
        )
      ) : null}
    </div>
  );
}

// Session ACW Loop 6: a single column below 375px (a KPI card's value + hint genuinely need the full width at that
// size), two columns from 375 to 639, then the existing sm/lg breakpoints take over unchanged. Tailwind has no
// default sub-375 breakpoint, hence the arbitrary min-[375px] variant rather than a new named one.
function TileGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 min-[375px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">{children}</div>;
}

function AttentionTiles({ tiles }: { tiles: DashboardTiles }) {
  const { shown, hiddenCount } = attentionTiles(kpiTileModels(tiles));
  return (
    <>
      {shown.length === 0 ? (
        <p className="text-sm text-ele-body" data-testid="nothing-needs-attention">
          {NOTHING_NEEDS_ATTENTION}
        </p>
      ) : (
        <TileGrid>
          {shown.map((m) => (
            <TileCard key={m.key} m={m} dashboard />
          ))}
        </TileGrid>
      )}
      <p className="mt-3 text-xs text-ele-body">
        <Link to="/office/reporting" className="text-ele-purple underline-offset-2 hover:underline max-sm:inline-flex max-sm:min-h-11 max-sm:items-center">
          All figures
        </Link>
        {hiddenCount > 0 && <span> ({hiddenCount} more {hiddenCount === 1 ? "needs" : "need"} attention)</span>}
      </p>
    </>
  );
}
